import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Package, AlertTriangle, Clock, TrendingUp, FileSearch,
  CheckCircle2, XCircle, Loader2, ChevronRight, RefreshCw, Factory,
  Star, Truck, FileSignature, Eye,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ==================== Types ====================

interface KPIs {
  openPOs: { count: number; value: number };
  pendingGRN: { count: number; value: number };
  overduePOs: {
    count: number;
    items: {
      id: string; number: string; vendor: string; total: number;
      deliveryDate: string; daysOverdue: number;
    }[];
  };
  activeQuotationJobs: number;
  lowStockAlerts: {
    count: number;
    items: {
      productId: string; name: string; sku: string;
      currentStock: number; minStock: number; maxStock: number | null; deficit: number;
    }[];
  };
  purchaseRequests: {
    count: number;
    items: any[];
  };
  expiringContracts: number;
  thisMonth: { poCount: number; poValue: number };
}

interface VendorPerformance {
  vendorId: string;
  name: string;
  rating: number | null;
  totalPOs: number;
  onTimePOs: number;
  onTimeRate: number | null;
  totalValue: number;
}

interface PurchaseRequest {
  id: string;
  prNumber?: string;
  productName?: string;
  productSku?: string;
  quantity?: number;
  unit?: string;
  urgency?: string;
  reason?: string;
  productionOrderId?: string;
  createdAt: string;
  isRead: boolean;
  title: string;
  body: string;
}

// ==================== Component ====================

export function PurchaseDashboardPage() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [vendorPerf, setVendorPerf] = useState<VendorPerformance[]>([]);
  const [purchaseReqs, setPurchaseReqs] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpiRes, perfRes, prRes] = await Promise.all([
        api.get('/purchase-dashboard/kpis'),
        api.get('/purchase-dashboard/vendor-performance'),
        api.get('/purchase-dashboard/purchase-requests?status=pending'),
      ]);
      setKpis(kpiRes.data.data);
      setVendorPerf(perfRes.data.data);
      setPurchaseReqs(prRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const acknowledgeRequest = async (id: string) => {
    try {
      await api.patch(`/purchase-dashboard/purchase-requests/${id}/acknowledge`);
      setPurchaseReqs((prev) => prev.filter((r) => r.id !== id));
      if (kpis) {
        setKpis({
          ...kpis,
          purchaseRequests: {
            count: Math.max(0, kpis.purchaseRequests.count - 1),
            items: kpis.purchaseRequests.items.filter((i: any) => i.id !== id),
          },
        });
      }
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  if (!kpis) return null;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Purchase Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Procurement overview and actionable alerts
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          icon={ShoppingCart}
          label="Open POs"
          value={kpis.openPOs.count}
          subValue={formatINR(kpis.openPOs.value)}
          color="blue"
          onClick={() => navigate('/purchase-orders')}
        />
        <KPICard
          icon={Package}
          label="Pending GRN"
          value={kpis.pendingGRN.count}
          subValue={formatINR(kpis.pendingGRN.value)}
          color="amber"
          onClick={() => navigate('/purchase-orders?status=confirmed')}
        />
        <KPICard
          icon={AlertTriangle}
          label="Overdue POs"
          value={kpis.overduePOs.count}
          subValue={kpis.overduePOs.count > 0 ? 'Action needed' : 'All on track'}
          color={kpis.overduePOs.count > 0 ? 'red' : 'green'}
        />
        <KPICard
          icon={FileSearch}
          label="Active RFQs"
          value={kpis.activeQuotationJobs}
          subValue="Quotation jobs"
          color="purple"
          onClick={() => navigate('/quotation-jobs')}
        />
      </div>

      {/* Secondary KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          icon={TrendingUp}
          label="This Month"
          value={kpis.thisMonth.poCount}
          subValue={formatINR(kpis.thisMonth.poValue)}
          color="green"
        />
        <KPICard
          icon={Factory}
          label="Purchase Requests"
          value={purchaseReqs.length}
          subValue={purchaseReqs.length > 0 ? 'From manufacturing' : 'No pending'}
          color={purchaseReqs.length > 0 ? 'orange' : 'gray'}
        />
        <KPICard
          icon={AlertTriangle}
          label="Low Stock"
          value={kpis.lowStockAlerts.count}
          subValue={kpis.lowStockAlerts.count > 0 ? 'Items below min' : 'Stock healthy'}
          color={kpis.lowStockAlerts.count > 0 ? 'red' : 'green'}
        />
        <KPICard
          icon={FileSignature}
          label="Expiring Contracts"
          value={kpis.expiringContracts}
          subValue="Within 30 days"
          color={kpis.expiringContracts > 0 ? 'amber' : 'gray'}
          onClick={() => navigate('/purchase-contracts')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Overdue POs */}
        <DashboardSection title="Overdue Purchase Orders" icon={Clock} count={kpis.overduePOs.count}>
          {kpis.overduePOs.items.length === 0 ? (
            <EmptyState message="No overdue POs" />
          ) : (
            <div className="space-y-2">
              {kpis.overduePOs.items.map((po) => (
                <div
                  key={po.id}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{po.number}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{po.vendor}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400">{po.daysOverdue}d overdue</p>
                    <p className="text-xs text-gray-500">{formatINR(po.total)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardSection>

        {/* Purchase Requests from Manufacturing */}
        <DashboardSection title="Purchase Requests" icon={Factory} count={purchaseReqs.length}>
          {purchaseReqs.length === 0 ? (
            <EmptyState message="No pending purchase requests" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {purchaseReqs.map((pr) => (
                <div
                  key={pr.id}
                  className="flex items-start justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {pr.prNumber || pr.title}
                      </p>
                      {pr.urgency && (
                        <UrgencyBadge urgency={pr.urgency} />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{pr.body}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(pr.createdAt)}</p>
                  </div>
                  <button
                    onClick={() => acknowledgeRequest(pr.id)}
                    className="ml-2 flex-shrink-0 p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                    title="Acknowledge"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </DashboardSection>

        {/* Low Stock Alerts */}
        <DashboardSection title="Low Stock Alerts" icon={AlertTriangle} count={kpis.lowStockAlerts.count}>
          {kpis.lowStockAlerts.items.length === 0 ? (
            <EmptyState message="All stock levels healthy" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {kpis.lowStockAlerts.items.map((item) => (
                <div
                  key={item.productId}
                  className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.sku || 'No SKU'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">
                      <span className="text-red-600 font-semibold">{item.currentStock}</span>
                      <span className="text-gray-400"> / {item.minStock} min</span>
                    </p>
                    <p className="text-xs text-red-500">Need +{item.deficit}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardSection>

        {/* Vendor On-Time Performance */}
        <DashboardSection title="Vendor On-Time Delivery" icon={Truck} count={vendorPerf.length}>
          {vendorPerf.length === 0 ? (
            <EmptyState message="No delivery data yet" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {vendorPerf.slice(0, 10).map((v) => (
                <div
                  key={v.vendorId}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => navigate(`/vendor-rate-comparison?vendorId=${v.vendorId}`)}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{v.name}</p>
                      <p className="text-xs text-gray-500">{v.totalPOs} POs | {formatINR(v.totalValue, false)}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    {v.rating && (
                      <div className="flex items-center gap-0.5 text-yellow-500">
                        <Star className="w-3 h-3 fill-current" />
                        <span className="text-xs">{v.rating}</span>
                      </div>
                    )}
                    <OnTimeRateBadge rate={v.onTimeRate} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardSection>
      </div>
    </div>
  );
}

// ==================== Sub Components ====================

const COLOR_MAP: Record<string, { bg: string; icon: string; text: string }> = {
  blue:   { bg: 'bg-blue-50 dark:bg-blue-900/20', icon: 'text-blue-600 dark:text-blue-400', text: 'text-blue-700 dark:text-blue-300' },
  green:  { bg: 'bg-green-50 dark:bg-green-900/20', icon: 'text-green-600 dark:text-green-400', text: 'text-green-700 dark:text-green-300' },
  red:    { bg: 'bg-red-50 dark:bg-red-900/20', icon: 'text-red-600 dark:text-red-400', text: 'text-red-700 dark:text-red-300' },
  amber:  { bg: 'bg-amber-50 dark:bg-amber-900/20', icon: 'text-amber-600 dark:text-amber-400', text: 'text-amber-700 dark:text-amber-300' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', icon: 'text-purple-600 dark:text-purple-400', text: 'text-purple-700 dark:text-purple-300' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', icon: 'text-orange-600 dark:text-orange-400', text: 'text-orange-700 dark:text-orange-300' },
  gray:   { bg: 'bg-gray-50 dark:bg-gray-700/50', icon: 'text-gray-500 dark:text-gray-400', text: 'text-gray-600 dark:text-gray-300' },
};

function KPICard({
  icon: Icon, label, value, subValue, color, onClick,
}: {
  icon: typeof ShoppingCart;
  label: string;
  value: number;
  subValue: string;
  color: string;
  onClick?: () => void;
}) {
  const c = COLOR_MAP[color] || COLOR_MAP.gray;
  return (
    <div
      className={`${c.bg} rounded-xl p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${c.icon}`} />
        {onClick && <ChevronRight className="w-4 h-4 text-gray-400" />}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className={`text-xs ${c.text} mt-1`}>{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{subValue}</p>
    </div>
  );
}

function DashboardSection({
  title, icon: Icon, count, children,
}: {
  title: string;
  icon: typeof Clock;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <span className="text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
          {count}
        </span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-6 text-sm text-gray-400 dark:text-gray-500">
      <CheckCircle2 className="w-4 h-4 mr-2" />
      {message}
    </div>
  );
}

function UrgencyBadge({ urgency }: { urgency: string }) {
  const styles: Record<string, string> = {
    low: 'bg-gray-100 text-gray-600',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[urgency] || styles.medium}`}>
      {urgency.toUpperCase()}
    </span>
  );
}

function OnTimeRateBadge({ rate }: { rate: number | null }) {
  if (rate === null) return <span className="text-xs text-gray-400">N/A</span>;
  const color = rate >= 80 ? 'text-green-600 bg-green-50' : rate >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${color}`}>
      {rate}%
    </span>
  );
}
